/**
 * Claude-style flat compose surface.
 *
 * Single rounded panel with a 1px border (no drop shadow, no
 * heavy focus ring) that holds:
 *   - an attachments strip (only rendered when there are pending
 *     drag-and-drop / paperclip attachments)
 *   - the textarea itself
 *   - a footer row with model / effort controls and the send/stop button
 *
 * The footer row keeps model controls next to the compose area;
 * live work metadata lives in the strip above.
 *
 * While the agent is active, the composer keeps the send button live
 * so prompts can be streamed to the SDK immediately, and shows a stop
 * button beside it for interrupting the current turn.
 *
 * Drag-and-drop attachments — drop any file anywhere on the agent
 * pane (image, PDF, datasheet, URDF, CAD/STEP, point cloud, …) or
 * paste an image / file into the textarea. Files are posted to
 * ``/workspace/agent/project/upload``, which lands them under
 * ``$TMPDIR/antioch-workspace-uploads/<short-id>-<name>``. Each
 * in-flight upload shows up as a chip with a progress bar; ready
 * chips ride along with the next user message as a leading
 * ``<attachments>`` sentinel block so the agent gets the absolute
 * paths and the webapp can parse them back out for display as
 * chips on the user-prompt bubble (see :class:`UserPrompt`).
 *
 * Pane-wide drag handling lives in :class:`AgentPanelOverlay`; this
 * component exposes an imperative ``ingestFiles`` handle via
 * ``apiRef`` so the overlay's drop handler can route bytes into the
 * same upload pipeline used by paste / paperclip / textarea drag.
 *
 * Key bindings:
 *   - Enter                  send
 *   - Shift+Enter            newline
 *   - Cmd/Ctrl+Enter         send
 *   - Escape                 interrupt (while busy)
 */

import { ArrowUp, FileText, Folder, ImageIcon, Paperclip, Square, TerminalSquare, X } from 'lucide-react';
import * as React from 'react';
import {
  type AgentDirectoryEntry,
  uploadFile as defaultUploadFile,
  fetchDirectoryEntries,
  type UploadedAttachment,
  type UploadOptions,
} from '@/lib/agent/api';
import type { ConnectionStatus } from '@/lib/agent/reducer';
import { workspaceScopedKey } from '@/lib/agent/session-persistence';
import type { SendInputResult } from '@/lib/agent/sessionRuntime';
import type { AgentSpeed, PermissionMode } from '@/lib/agent/types';
import { compressImageForUpload } from '@/lib/media-utils/imageUploadCompression';
import { cn } from '@/lib/utils/cn';
import {
  attachmentUploadUrl,
  isImageAttachment,
  pastedSnippetFilename,
  shouldAttachPastedText,
} from './attachmentUtils';
import {
  DecoratedInputText,
  inputTokenDecorations,
  type MentionRange,
  reconcileMentionRanges,
  shiftMentionRanges,
  trimMentionToken,
  validateMentionRanges,
} from './inputDecorations';
import { EffortPill, PermissionModePill, SpeedPill } from './StatusChips';

const MAX_FILE_BYTES = 512 * 1024 * 1024;
const DUPLICATE_SUBMIT_WINDOW_MS = 800;

/**
 * ``localStorage`` namespace for in-flight chat drafts. Each
 * ``(cwd, sessionId)`` pair gets its own key under this prefix so
 * the user can park a half-typed thought, hop to another session,
 * and come back to find it intact.
 */
const DRAFT_KEY_PREFIX = 'antioch:agent-draft:';

function readDraft(key: string | null): string {
  if (!key || typeof window === 'undefined') return '';
  try {
    return window.localStorage.getItem(key) ?? '';
  } catch {
    return '';
  }
}

function writeDraft(key: string | null, value: string): void {
  if (!key || typeof window === 'undefined') return;
  try {
    if (value.length === 0) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    // Quota exceeded / private mode / disabled storage — drafts
    // simply don't persist this session, which is graceful
    // enough that a try/catch with no fallback is correct.
  }
}

/**
 * Snapshot of the identifying triple ``(draftKey, cwd, sessionId)``
 * at the moment the in-memory ``value`` was last associated with a
 * key. Tracked on a ref so the draft-migration effect can decide
 * whether a ``draftKey`` change is a session switch (hydrate from
 * the new key) or a fresh session's id materializing
 * (carry the in-progress value across).
 */
export interface DraftKeyContext {
  key: string | null;
  workspaceId: string | null | undefined;
  cwd: string | null | undefined;
  sessionId: string | null | undefined;
}

export type DraftKeyTransition =
  | { kind: 'unchanged' }
  | { kind: 'fresh-materialization'; previousKey: string | null }
  | { kind: 'switch' };

/**
 * Classify a ``(previous, next)`` draft-key transition. Pulled out
 * as a pure helper so the migration / hydration decision can be
 * regression-tested without driving a full render loop.
 *
 * * ``unchanged`` -- key is identical, no-op.
 * * ``fresh-materialization`` -- same workspace and cwd, previous
 *   attached as a fresh session (``sessionId == null``) and next has
 *   a real id. The user was typing into ``:new``; the server just
 *   told us the real id. We migrate by clearing the ``:new`` slot and
 *   keeping the in-memory value, never reading the new key (which
 *   may contain a stale "just submitted" artifact from a prior
 *   migration race).
 * * ``switch`` -- everything else (workspace changed, cwd changed,
 *   sidebar session pick, runtime rebind to a different id). Hydrate
 *   from the new key. A workspace change is always a switch: two
 *   workspaces sharing a cwd must never carry a draft across.
 */
export function classifyDraftKeyTransition(previous: DraftKeyContext, next: DraftKeyContext): DraftKeyTransition {
  if (previous.key === next.key) return { kind: 'unchanged' };
  const isFreshMaterialization =
    previous.workspaceId === next.workspaceId &&
    previous.cwd === next.cwd &&
    previous.sessionId == null &&
    typeof next.sessionId === 'string';
  if (isFreshMaterialization) return { kind: 'fresh-materialization', previousKey: previous.key };
  return { kind: 'switch' };
}

type CompletionMode = 'slash' | 'path';

interface CompletionItem {
  id: string;
  mode: CompletionMode;
  label: string;
  value: string;
  isDir?: boolean;
}

interface SlashTrigger {
  query: string;
  start: number;
  end: number;
}

interface PathTrigger {
  query: string;
  start: number;
  end: number;
}

export type UploadFn = (file: File, opts: UploadOptions) => Promise<UploadedAttachment>;

/**
 * Imperative handle exposed by ``AgentInput`` so ancestors (the
 * overlay's pane-wide drop handler) can push files into the same
 * upload pipeline that paste / paperclip / drop-on-input use.
 */
export interface AgentInputApi {
  ingestFiles: (files: FileList | File[] | null | undefined) => void;
  /**
   * Replace the composer text with ``text`` (or append it on a new
   * line when ``mode`` is ``'append'``), focus the textarea, and
   * move the caret to the end so the user can immediately edit or
   * send. Used as a safe fallback when a starter prompt cannot send.
   */
  setText: (text: string, mode?: 'replace' | 'append') => void;
}

interface Attachment {
  id: string;
  name: string;
  size: number;
  mime: string;
  status: 'uploading' | 'ready' | 'error';
  progress: number;
  path?: string;
  error?: string;
  /** Object URL for image thumbnails — created lazily, revoked on remove. */
  previewUrl?: string;
  controller?: AbortController;
}

interface AttachmentStore {
  attachments: Attachment[];
  listeners: Set<() => void>;
}

const ATTACHMENT_STORES = new Map<string, AttachmentStore>();

/**
 * Build the draft / attachment-store key for a ``(workspaceId, cwd,
 * sessionId)`` triple. ``workspaceId`` is folded in -- mirroring
 * :func:`runtimeKey` and the ``session-persistence`` scoping -- because
 * different workspaces routinely share a ``cwd`` path, so without it a
 * brand-new-session draft (``sessionId === null`` -> ``new``) typed in
 * one workspace would bleed into the same-``cwd`` fresh composer of
 * another. The NUL separator cannot occur in a workspace id or cwd.
 */
export function attachmentStoreKey(workspaceId: string | null, cwd: string, sessionId: string | null): string {
  return `${DRAFT_KEY_PREFIX}${workspaceScopedKey(workspaceId, cwd)}:${sessionId ?? 'new'}`;
}

/**
 * Drop the attachment store for a ``(cwd, sessionId)`` pair and revoke any
 * lingering thumbnail blob URLs on the way out. Called from the
 * AgentPanelProvider whenever the matching session runtime is evicted from
 * the LRU cache so {@link ATTACHMENT_STORES} can't grow without bound on a
 * long-running tab. The remove / submit paths already revoke ``previewUrl``s
 * for attachments the user interacted with; this final sweep catches any the
 * user uploaded, never sent, and walked away from before the runtime was
 * disposed.
 *
 * :param workspaceId: Workspace the attachment store was scoped to, or ``null``.
 * :param cwd: Project cwd the attachment store was scoped to.
 * :param sessionId: Session id, or ``null`` for the brand-new-session draft slot.
 */
export function disposeAttachmentStore(workspaceId: string | null, cwd: string, sessionId: string | null): void {
  const key = attachmentStoreKey(workspaceId, cwd, sessionId);
  const store = ATTACHMENT_STORES.get(key);
  if (!store) return;
  for (const att of store.attachments) {
    if (att.previewUrl) URL.revokeObjectURL(att.previewUrl);
  }
  ATTACHMENT_STORES.delete(key);
}

function attachmentStore(key: string): AttachmentStore {
  let store = ATTACHMENT_STORES.get(key);
  if (!store) {
    store = { attachments: [], listeners: new Set() };
    ATTACHMENT_STORES.set(key, store);
  }
  return store;
}

function subscribeAttachments(key: string, listener: () => void): () => void {
  const store = attachmentStore(key);
  store.listeners.add(listener);
  return () => {
    store.listeners.delete(listener);
  };
}

function getAttachmentsSnapshot(key: string): Attachment[] {
  return attachmentStore(key).attachments;
}

function updateAttachments(key: string, update: (prev: Attachment[]) => Attachment[]): void {
  const store = attachmentStore(key);
  const next = update(store.attachments);
  if (next === store.attachments) return;
  store.attachments = next;
  for (const listener of store.listeners) listener();
}

function moveAttachments(fromKey: string, toKey: string): void {
  if (fromKey === toKey) return;
  const from = attachmentStore(fromKey);
  if (from.attachments.length === 0) return;
  const to = attachmentStore(toKey);
  if (to.attachments.length > 0) return;
  to.attachments = from.attachments;
  from.attachments = [];
  for (const listener of from.listeners) listener();
  for (const listener of to.listeners) listener();
}

function patchStoredAttachment(preferredKey: string, id: string, patch: Partial<Attachment>): void {
  if (patchStoreAttachment(attachmentStore(preferredKey), id, patch)) return;
  for (const [key, store] of ATTACHMENT_STORES) {
    if (key === preferredKey) continue;
    if (patchStoreAttachment(store, id, patch)) return;
  }
}

function patchStoreAttachment(store: AttachmentStore, id: string, patch: Partial<Attachment>): boolean {
  let changed = false;
  const next = store.attachments.map((attachment) => {
    if (attachment.id !== id) return attachment;
    changed = true;
    return { ...attachment, ...patch };
  });
  if (!changed) return false;
  store.attachments = next;
  for (const listener of store.listeners) listener();
  return true;
}

function sendAccepted(result: SendInputResult | undefined): result is Exclude<SendInputResult, false> {
  return result !== false && result !== undefined;
}

interface PendingSubmitState {
  clientMessageId: string;
  submittedAttachmentIds: string[];
  uploadKey: string;
  draftKey: string | null;
}

function clearSubmittedDraft(
  setValue: React.Dispatch<React.SetStateAction<string>>,
  setCursorIndex: React.Dispatch<React.SetStateAction<number>>,
  setConfirmedMentions: React.Dispatch<React.SetStateAction<MentionRange[]>>,
  uploadKey: string,
  attachmentIds: string[]
): void {
  setValue('');
  setCursorIndex(0);
  setConfirmedMentions([]);
  clearSubmittedAttachments(uploadKey, attachmentIds);
}

function clearSubmittedAttachments(uploadKey: string, attachmentIds: string[]): void {
  updateAttachments(uploadKey, (prev) => {
    for (const att of prev) {
      if (attachmentIds.includes(att.id) && att.previewUrl) URL.revokeObjectURL(att.previewUrl);
    }
    return prev.filter((att) => !attachmentIds.includes(att.id));
  });
}

export interface AgentInputProps {
  busy?: boolean;
  /** Prevent sending while preserving the draft, e.g. during a foreground tool run. */
  sendBlocked?: boolean;
  sendBlockedReason?: string | null;
  onSend: (text: string) => SendInputResult | undefined;
  onInterrupt?: () => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;

  /**
   * Project cwd for uploads. When ``null`` drag-and-drop and the
   * paperclip button are disabled — we have nowhere to send the
   * bytes until the user has picked a project.
   */
  cwd?: string | null;

  /**
   * Override for the upload implementation. Stories and tests
   * inject a mock; production passes nothing and we use the real
   * :func:`uploadFile` in ``api.ts``.
   */
  uploadFn?: UploadFn;

  /** Connection state gates controls that reconnect the active session. */
  connectionStatus?: ConnectionStatus;
  attached?: boolean;
  pendingUserInputIds?: string[];
  lastAcceptedClientMessageId?: string | null;
  /** Footer-row chips. All optional — chips render only when set. */
  /** Reasoning effort level (e.g. "xhigh"). Rendered as a stylized chip. */
  effort?: string | null;
  /**
   * Optional setter for the reasoning effort. When provided, the
   * effort chip becomes a clickable dropdown; the callback fires
   * with the newly picked level (e.g. ``"high"``). Omit to render
   * a static label.
   */
  onChangeEffort?: (effort: string) => void;
  /** Current Claude Code speed mode ("normal" | "fast"). */
  speed?: AgentSpeed | string | null;
  onChangeSpeed?: (speed: AgentSpeed) => void;
  permissionMode?: PermissionMode | string | null;
  onChangePermissionMode?: (mode: PermissionMode) => void;
  canChangePermissionMode?: boolean;
  /**
   * Slash command names advertised by the spawned CLI (without
   * leading ``/``). Drives the autocomplete popover that opens when
   * the user types ``/`` at the start of the textarea. Sending a
   * matching command rides the normal ``user_input`` path — the SDK
   * recognises ``/<name>`` and dispatches it the same way the CLI
   * does.
   */
  slashCommands?: string[];

  /**
   * Optional imperative handle. When provided, the input writes its
   * :class:`AgentInputApi` into ``apiRef.current`` on mount and
   * clears it on unmount, so an ancestor (the overlay) can push
   * files in from a pane-wide drop handler.
   */
  apiRef?: React.MutableRefObject<AgentInputApi | null>;

  /**
   * Identifier used to persist the in-progress draft in
   * ``localStorage`` so the user never loses a half-typed message
   * across reloads / project switches / hopping between sessions.
   * Pass the active session id when one exists; pass ``null`` for
   * the "new session" composer slot. ``workspaceId`` and ``cwd`` both
   * participate in the key: ``cwd`` so the same session id under
   * different projects keeps separate drafts, and ``workspaceId`` so
   * two workspaces sharing a ``cwd`` don't share the fresh-session
   * (``new``) draft slot.
   */
  draftKeyWorkspaceId?: string | null;
  draftKeyCwd?: string | null;
  draftKeySessionId?: string | null;

  /**
   * Notified synchronously inside the textarea-grow
   * ``useLayoutEffect`` whenever the composer's measured height
   * changes. The agent panel forwards this to
   * :attr:`TurnStreamProps.keepAtBottomRef` so the chat re-pins to
   * the bottom in the same commit -- otherwise a one-frame "chat
   * scrolled up 22px" appears between the textarea growing and
   * ``ResizeObserver`` catching up, which flickers the Latest pill
   * and the scrollbar.
   */
  onComposerHeightChange?: () => void;
}

export function AgentInput({
  busy = false,
  sendBlocked = false,
  sendBlockedReason = null,
  onSend,
  onInterrupt,
  placeholder,
  disabled = false,
  className,
  cwd,
  uploadFn = defaultUploadFile,
  connectionStatus,
  attached = false,
  pendingUserInputIds = [],
  lastAcceptedClientMessageId = null,
  effort,
  onChangeEffort,
  speed,
  onChangeSpeed,
  permissionMode,
  onChangePermissionMode,
  canChangePermissionMode = false,
  slashCommands,
  apiRef,
  draftKeyWorkspaceId,
  draftKeyCwd,
  draftKeySessionId,
  onComposerHeightChange,
}: AgentInputProps) {
  // Compute the storage key for the current ``(workspaceId, cwd,
  // sessionId)`` triple. ``null`` here means "no key — don't persist"
  // and is the safety hatch for stories / tests that don't pass
  // identifiers. Shares :func:`attachmentStoreKey` so the localStorage
  // draft text and the in-memory attachment store are keyed identically.
  const draftKey = React.useMemo(() => {
    if (!draftKeyCwd) return null;
    return attachmentStoreKey(draftKeyWorkspaceId ?? null, draftKeyCwd, draftKeySessionId ?? null);
  }, [draftKeyWorkspaceId, draftKeyCwd, draftKeySessionId]);
  const localUploadKey = React.useId();
  const uploadKey = draftKey ?? `local:${localUploadKey}`;
  const previousUploadTargetRef = React.useRef<{
    key: string;
    workspaceId: string | null | undefined;
    cwd: string | null | undefined;
    sessionId: string | null | undefined;
  }>({ key: uploadKey, workspaceId: draftKeyWorkspaceId, cwd: draftKeyCwd, sessionId: draftKeySessionId });
  React.useEffect(() => {
    const previous = previousUploadTargetRef.current;
    // Same transition rules as the draft-text migration below (one
    // classifier, one definition of "fresh session materialized"): a
    // workspace change is never a materialization — two workspaces
    // sharing a cwd must not move attachments from one's ``:new`` slot
    // into the other's session.
    const transition = classifyDraftKeyTransition(previous, {
      key: uploadKey,
      workspaceId: draftKeyWorkspaceId,
      cwd: draftKeyCwd,
      sessionId: draftKeySessionId,
    });
    if (transition.kind === 'fresh-materialization') {
      moveAttachments(previous.key, uploadKey);
      setPendingSubmit((pending) =>
        pending?.uploadKey === previous.key ? { ...pending, uploadKey, draftKey } : pending
      );
    }
    previousUploadTargetRef.current = {
      key: uploadKey,
      workspaceId: draftKeyWorkspaceId,
      cwd: draftKeyCwd,
      sessionId: draftKeySessionId,
    };
  }, [draftKey, draftKeyWorkspaceId, draftKeyCwd, draftKeySessionId, uploadKey]);
  const attachments = React.useSyncExternalStore(
    React.useCallback((listener) => subscribeAttachments(uploadKey, listener), [uploadKey]),
    React.useCallback(() => getAttachmentsSnapshot(uploadKey), [uploadKey]),
    React.useCallback(() => getAttachmentsSnapshot(uploadKey), [uploadKey])
  );

  // Hydrate ``value`` from the persisted draft for this slot on
  // first render and whenever ``draftKey`` changes (e.g. user
  // hopped to another session). Reading ``localStorage`` lazily
  // inside ``useState`` initializers avoids a paint with empty
  // content followed by a flash to the restored draft.
  const [value, setValue] = React.useState(() => readDraft(draftKey));
  // Spans of ``@path`` mentions the user actually picked from the
  // completion menu. Only these get the emerald attachment styling and
  // expand into ``<context_paths>`` on send -- a bare ``@word`` typed or
  // pasted into the body (``@Linear`` from a Slack quote) stays plain
  // text. This is genuine UI state with no source-of-truth home: the
  // textarea owns the string, but "which spans were confirmed" is a fact
  // about user intent that only the composer observes, so it lives here
  // and is reconciled against every value change.
  const [confirmedMentions, setConfirmedMentions] = React.useState<MentionRange[]>([]);
  const recentSubmitRef = React.useRef<{ text: string; at: number } | null>(null);
  const [pendingSubmit, setPendingSubmit] = React.useState<PendingSubmitState | null>(null);
  // Track the identifying triple ``(draftKey, cwd, sessionId)`` the
  // current ``value`` belongs to. The migration effect below uses
  // it to classify a ``draftKey`` change as either a session
  // switch (hydrate fresh from the new key) or a fresh-session id
  // materialization (carry the in-memory value across, clear the
  // stale ``:new`` slot). The persistence effect uses it to refuse
  // writing ``value`` to a freshly-flipped ``draftKey`` until the
  // migration effect has confirmed the two are aligned -- without
  // that guard, a same-render flip can plant the pre-migration
  // value into the new key, which a later draftKey glitch then
  // rehydrates back into the input (the "submitted but text
  // stays" race).
  const valueDraftContextRef = React.useRef<DraftKeyContext>({
    key: draftKey,
    workspaceId: draftKeyWorkspaceId,
    cwd: draftKeyCwd,
    sessionId: draftKeySessionId,
  });
  React.useEffect(() => {
    const previous = valueDraftContextRef.current;
    const next: DraftKeyContext = {
      key: draftKey,
      workspaceId: draftKeyWorkspaceId,
      cwd: draftKeyCwd,
      sessionId: draftKeySessionId,
    };
    const transition = classifyDraftKeyTransition(previous, next);
    if (transition.kind === 'unchanged') return;
    if (transition.kind === 'fresh-materialization') {
      // Wipe the ``:new`` slot so a future fresh session in the
      // same cwd doesn't ghost-resurrect this draft, but keep
      // the in-memory value: the user was typing into ``:new``
      // and would otherwise lose what's in the textarea.
      if (transition.previousKey) writeDraft(transition.previousKey, '');
      valueDraftContextRef.current = next;
      return;
    }
    const restored = readDraft(draftKey);
    setValue(restored);
    setCursorIndex(restored.length);
    // Drafts persist as raw strings, so a restored draft carries no
    // confirmation metadata -- any ``@token`` in it is treated as plain
    // text until re-picked. Sending it still passes the literal mention
    // through to the agent, so this degrades gracefully.
    setConfirmedMentions([]);
    valueDraftContextRef.current = next;
  }, [draftKey, draftKeyWorkspaceId, draftKeyCwd, draftKeySessionId]);

  // Persist the draft under the active key on every keystroke.
  // ``localStorage`` writes are synchronous but tiny — typing
  // doesn't measurably suffer at this size, and debouncing would
  // risk losing the last few characters on a hard refresh.
  //
  // The ``valueDraftContextRef`` gate is load-bearing: when
  // ``draftKey`` flips in the same render as a stale ``value``
  // (e.g. the user submits a message, then ``state.sessionId``
  // materializes), this effect would otherwise write the
  // about-to-be-cleared text to the new key before the migration
  // effect's ``setValue`` commits. A subsequent draftKey echo
  // (reconnect, settings flip) would then read that planted text
  // back into the input. Refusing the write until the context
  // ref matches keeps the new key clean.
  React.useEffect(() => {
    if (valueDraftContextRef.current.key !== draftKey) return;
    writeDraft(draftKey, value);
  }, [draftKey, value]);
  React.useEffect(() => {
    if (!pendingSubmit) return;
    if (lastAcceptedClientMessageId !== pendingSubmit.clientMessageId) return;
    if (pendingSubmit.uploadKey === uploadKey) {
      clearSubmittedDraft(
        setValue,
        setCursorIndex,
        setConfirmedMentions,
        uploadKey,
        pendingSubmit.submittedAttachmentIds
      );
    } else {
      writeDraft(pendingSubmit.draftKey, '');
      clearSubmittedAttachments(pendingSubmit.uploadKey, pendingSubmit.submittedAttachmentIds);
    }
    setPendingSubmit(null);
  }, [lastAcceptedClientMessageId, pendingSubmit, uploadKey]);
  React.useEffect(() => {
    if (!pendingSubmit) return;
    if (pendingUserInputIds.includes(pendingSubmit.clientMessageId)) return;
    if (lastAcceptedClientMessageId === pendingSubmit.clientMessageId) return;
    setPendingSubmit(null);
  }, [lastAcceptedClientMessageId, pendingSubmit, pendingUserInputIds]);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [cursorIndex, setCursorIndex] = React.useState(() => value.length);

  const readCursorIndex = React.useCallback(() => {
    const textarea = textareaRef.current;
    setCursorIndex(textarea?.selectionStart ?? value.length);
  }, [value.length]);

  // After a refresh/session restore, put the cursor back in the
  // composer so the agent pane is ready for the next prompt. The
  // active-element guard avoids stealing focus if the user already
  // clicked a sidebar/session control while the chat was mounting.
  React.useEffect(() => {
    if (disabled) return;
    const active = document.activeElement;
    if (active && active !== document.body && active !== document.documentElement) return;

    const rafId = requestAnimationFrame(() => {
      const textarea = textareaRef.current;
      if (!textarea || textarea.disabled) return;
      textarea.focus({ preventScroll: true });
      const end = textarea.value.length;
      textarea.setSelectionRange(end, end);
      setCursorIndex(end);
    });

    return () => cancelAnimationFrame(rafId);
  }, [disabled]);

  // Completion picker state. Slash commands open for a leading
  // ``/<query>`` command; path mentions open for the token directly
  // before the cursor matching ``@<path-fragment>``. The path menu
  // asks the workspace server for directory entries and inserts a
  // plain ``@path`` mention into the textarea. On send, those
  // mentions are expanded into a hidden ``<context_paths>`` block
  // so Claude sees explicit workspace paths.
  //
  // Only show commands the spawned CLI advertised. Guessing built-ins
  // here produced menu entries that some workspaces reject (e.g.
  // ``/clear``), which is worse than temporarily showing no command
  // menu before ``system.init`` arrives.
  const effectiveCommands = slashCommands ?? [];
  const slashTrigger = React.useMemo(() => slashTriggerAt(value, cursorIndex), [value, cursorIndex]);
  const pathTrigger = React.useMemo(() => pathTriggerAt(value, cursorIndex), [value, cursorIndex]);
  const slashItems = React.useMemo<CompletionItem[]>(() => {
    if (!slashTrigger) return [];
    const query = slashTrigger.query.toLowerCase();
    return effectiveCommands
      .filter((name) => name.toLowerCase().startsWith(query))
      .slice(0, 8)
      .map((name) => ({
        id: `slash:${name}`,
        mode: 'slash',
        label: name,
        value: name,
      }));
  }, [slashTrigger, effectiveCommands]);
  const [pathItems, setPathItems] = React.useState<CompletionItem[]>([]);
  React.useEffect(() => {
    if (!cwd || !pathTrigger) {
      setPathItems([]);
      return;
    }

    const request = pathCompletionRequest(cwd, pathTrigger.query);
    let cancelled = false;
    const timer = setTimeout(() => {
      void fetchDirectoryEntries(request.directory, {
        showHidden: request.prefix.startsWith('.'),
        includeFiles: true,
      })
        .then((listing) => {
          if (cancelled) return;
          const prefix = request.prefix.toLowerCase();
          const next = listing.entries
            .filter((entry) => entry.name.toLowerCase().startsWith(prefix))
            .sort(comparePathEntries)
            .slice(0, 8)
            .map((entry) => pathCompletionItem(entry, request.insertPrefix));
          setPathItems(next);
        })
        .catch(() => {
          if (!cancelled) setPathItems([]);
        });
    }, 80);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [cwd, pathTrigger]);

  const completionMode: CompletionMode | null = slashItems.length > 0 ? 'slash' : pathItems.length > 0 ? 'path' : null;
  const completionItems = completionMode === 'slash' ? slashItems : completionMode === 'path' ? pathItems : [];
  const completionOpen = completionItems.length > 0;
  const [completionIndexRaw, setCompletionIndex] = React.useState(0);
  const completionSignature = completionItems.map((item) => item.id).join('\u0000');
  // biome-ignore lint/correctness/useExhaustiveDependencies: the signature is the trigger; the effect body only resets local highlight state
  React.useEffect(() => {
    setCompletionIndex(0);
  }, [completionMode, completionSignature]);
  // Clamp during render so the index can never reference a row the
  // current completion list no longer has — the alternative (an
  // effect that resets on every keystroke) churned the menu's
  // highlight before the user could see it.
  const completionIndex =
    completionItems.length > 0
      ? ((completionIndexRaw % completionItems.length) + completionItems.length) % completionItems.length
      : 0;
  const inputLocked = pendingSubmit !== null;
  const composerDisabled = disabled || inputLocked;
  const uploadsDisabled = composerDisabled || !cwd;

  // Auto-grow the textarea to fit ``value``, capped at ``max-h-[400px]``
  // (keep that cap in lock-step with the Tailwind class on the
  // textarea below — ~400px is roughly 16 lines of body text at
  // the current font size, generous enough for a long paragraph
  // or a pasted snippet while still leaving the chat itself the
  // dominant surface in the panel).
  //
  // This is intentionally driven off ``[value]`` rather than only
  // mount + onChange so every value transition resizes the box —
  // user typing, draft restore on session switch, programmatic
  // ``setValue('')`` after send, slash-command pick. Driving them
  // through the same effect closes a real bug: after a draft
  // restore the inline ``style.height`` would be stale from the
  // previous session's content, and the box would render the
  // wrong size until the next keystroke triggered a manual
  // re-measure — which read as "the input shrinks the first
  // time I click into it."
  //
  // ``useLayoutEffect`` runs synchronously after the DOM mutation
  // but before paint, so the user never sees a frame of mis-
  // sized textarea.
  //
  // The body doesn't syntactically reference ``value`` (it reads
  // ``scrollHeight`` off the DOM) — it's listed as a dep because
  // the DOM's scrollHeight only changes when the controlled
  // value does, which is exactly when we need to re-measure.
  // biome-ignore lint/correctness/useExhaustiveDependencies: ``value`` is the trigger for re-measuring scrollHeight
  React.useLayoutEffect(() => {
    const t = textareaRef.current;
    if (!t) return;
    // Always do the auto -> measured ping-pong, even when typing
    // doesn't change the final textarea height. Without the ``auto``
    // reset, ``scrollHeight`` can't shrink (it's clamped to the
    // explicit ``style.height``), so a delete on a 5-line textarea
    // would leave it 5 lines tall forever.
    t.style.height = 'auto';
    t.style.height = `${Math.min(t.scrollHeight, 400)}px`;
    // Always notify the chat container, even when the height
    // didn't move. The ``height = 'auto'`` step above briefly
    // collapses the textarea to ~1 line; during that intra-effect
    // moment the chat's ``clientHeight`` grows by that delta and
    // the browser auto-clamps the chat's ``scrollTop`` downward
    // to keep it inside the now-larger scroll range. Restoring
    // ``style.height`` to the measured value shrinks the chat
    // back -- but ``scrollTop`` does NOT auto-restore, so without
    // a re-pin the chat would sit one textarea's worth of pixels
    // above the floor for the rest of the typing session. The
    // chat's ``keepAtBottom`` callback uses a NON-race-safe pin
    // (``pinToBottomThroughHeightChange`` in ``TurnStream``) that
    // trusts ``followingTailRef`` rather than reading the
    // mid-effect ``scrollTop`` -- the race-safe path used by the
    // ResizeObserver would misread the auto-clamp as a deliberate
    // wheel-up and release follow.
    onComposerHeightChange?.();
  }, [value, onComposerHeightChange]);

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    // Height is owned by the ``[value]`` layout effect above so
    // every value transition (typing, paste, draft restore,
    // post-send clear) goes through the same resize path. We
    // don't need to poke ``style.height`` here.
    const nextValue = e.target.value;
    // Carry confirmed mention spans across the edit before swapping the
    // value. A paste that merely contains ``@Linear`` adds no new span,
    // so it never gains the attachment styling.
    setConfirmedMentions((prev) => reconcileMentionRanges(value, nextValue, prev));
    setValue(nextValue);
    setCursorIndex(e.target.selectionStart ?? nextValue.length);
    syncMirrorScroll();
  }

  // Sync the highlight mirror's scroll position to the textarea.
  // The mirror sits behind the textarea with identical typography
  // and padding; without this the styled slash prefix drifts out
  // of register the moment the value overflows the visible area.
  const mirrorRef = React.useRef<HTMLDivElement>(null);
  function syncMirrorScroll() {
    const ta = textareaRef.current;
    const mirror = mirrorRef.current;
    if (!ta || !mirror) return;
    mirror.scrollTop = ta.scrollTop;
    mirror.scrollLeft = ta.scrollLeft;
  }

  // Decoration mirror for slash commands and @path mentions. The
  // mirror keeps exact textarea metrics: same font, same weight, no
  // padding added to decorated tokens. The badge look is drawn with
  // background/shadow only, so caret position stays aligned with
  // the real textarea text layer.
  const inputDecorations = React.useMemo(
    () => inputTokenDecorations(value, confirmedMentions),
    [value, confirmedMentions]
  );
  const hasInputDecorations = inputDecorations.length > 0;

  function buildOutgoingText(text: string, ready: Attachment[], contextPaths: string[]): string {
    let outgoing = text;
    if (contextPaths.length > 0) {
      const contextBlock = `<context_paths>\n${contextPaths.join('\n')}\n</context_paths>`;
      outgoing = outgoing ? `${contextBlock}\n\n${outgoing}` : contextBlock;
    }
    if (ready.length === 0) return outgoing;
    // Wrap the paths in an ``<attachments>`` sentinel block. Claude
    // reads XML-like wrappers natively (it's a long-standing prompt
    // convention) so the agent still sees the absolute paths plain;
    // the webapp parses the same block back out in ``UserPrompt`` to
    // render attachment chips on the user bubble instead of leaking
    // the raw paths into the conversation transcript.
    const lines = ready.map((a) => a.path).join('\n');
    const block = `<attachments>\n${lines}\n</attachments>`;
    return outgoing ? `${block}\n\n${outgoing}` : block;
  }

  function send() {
    if (sendBlocked || pendingSubmit) return;
    const trimmed = value.trim();
    const ready = attachments.filter((a) => a.status === 'ready');
    if (!trimmed && ready.length === 0) return;
    if (attachments.some((a) => a.status === 'uploading')) return;
    // Resolve context paths from the confirmed spans against the raw
    // (untrimmed) value -- the spans index into ``value``, so trimming
    // first would misalign their offsets.
    const contextPaths = cwd ? resolveMentionPaths(value, cwd, confirmedMentions) : [];
    const outgoing = buildOutgoingText(trimmed, ready, contextPaths);
    const now = Date.now();
    const recent = recentSubmitRef.current;
    if (recent && recent.text === outgoing && now - recent.at < DUPLICATE_SUBMIT_WINDOW_MS) return;
    recentSubmitRef.current = { text: outgoing, at: now };
    const sent = onSend(outgoing);
    if (!sendAccepted(sent)) {
      recentSubmitRef.current = null;
      return;
    }
    const submittedAttachmentIds = ready.map((att) => att.id);
    if (sent.clientMessageId && !sent.acknowledged) {
      setPendingSubmit({
        clientMessageId: sent.clientMessageId,
        submittedAttachmentIds,
        uploadKey,
        draftKey,
      });
      return;
    }
    clearSubmittedDraft(setValue, setCursorIndex, setConfirmedMentions, uploadKey, submittedAttachmentIds);
  }

  function replaceInputRange(start: number, end: number, replacement: string) {
    const next = `${value.slice(0, start)}${replacement}${value.slice(end)}`;
    const cursor = start + replacement.length;
    // A range replacement is a single contiguous edit, so reuse the same
    // span bookkeeping the keystroke path uses: shift mentions after the
    // edit, drop any the edit reached into.
    setConfirmedMentions((prev) =>
      validateMentionRanges(next, shiftMentionRanges(prev, start, end, replacement.length))
    );
    setValue(next);
    setCursorIndex(cursor);
    requestAnimationFrame(() => {
      const t = textareaRef.current;
      if (!t) return;
      t.focus();
      t.setSelectionRange(cursor, cursor);
    });
  }

  function pickSlash(name: string) {
    if (!slashTrigger) return;
    replaceInputRange(slashTrigger.start, slashTrigger.end, `/${name}`);
  }

  function pickPath(item: CompletionItem) {
    if (!pathTrigger) return;
    // Confirm the picked span. ``replaceInputRange`` first shifts the
    // existing mentions for this edit; appending the new span afterwards
    // is the only place a ``@path`` mention is ever marked confirmed.
    const start = pathTrigger.start;
    const mention = `@${item.value}`;
    replaceInputRange(start, pathTrigger.end, `${mention} `);
    setConfirmedMentions((prev) => [...prev, { start, end: start + mention.length }].sort((a, b) => a.start - b.start));
  }

  function pickCompletion(item: CompletionItem, action: 'insert' | 'send') {
    if (item.mode === 'slash') {
      if (action === 'send') {
        if (sendBlocked || pendingSubmit) return;
        const sent = onSend(`/${item.value}`);
        if (!sendAccepted(sent)) return;
        if (sent.clientMessageId && !sent.acknowledged) {
          setPendingSubmit({ clientMessageId: sent.clientMessageId, submittedAttachmentIds: [], uploadKey, draftKey });
          return;
        }
        clearSubmittedDraft(setValue, setCursorIndex, setConfirmedMentions, uploadKey, []);
      } else {
        pickSlash(item.value);
      }
      return;
    }
    pickPath(item);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    // Completion picker keyboard model: ArrowUp/Down navigates,
    // Tab fills the current selection (replaces the textarea content
    // with ``/<name>`` or ``@path``), Enter sends slash commands
    // directly but inserts path mentions, Escape closes by removing
    // the trigger character.
    if (completionOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Down') {
        e.preventDefault();
        e.stopPropagation();
        setCompletionIndex((i) => (i + 1) % completionItems.length);
        return;
      }
      if (e.key === 'ArrowUp' || e.key === 'Up') {
        e.preventDefault();
        e.stopPropagation();
        setCompletionIndex((i) => (i - 1 + completionItems.length) % completionItems.length);
        return;
      }
      if (e.key === 'Tab') {
        e.preventDefault();
        const picked = completionItems[completionIndex] ?? completionItems[0];
        if (picked) pickCompletion(picked, 'insert');
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        if (slashTrigger) replaceInputRange(slashTrigger.start, slashTrigger.start + 1, '');
        else if (pathTrigger) replaceInputRange(pathTrigger.start, pathTrigger.start + 1, '');
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey && !(e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      if (sendBlocked) return;
      // When the picker is open, Enter sends highlighted slash
      // commands but inserts highlighted path mentions.
      if (completionOpen) {
        const picked = completionItems[completionIndex] ?? completionItems[0];
        if (picked) {
          pickCompletion(picked, picked.mode === 'slash' ? 'send' : 'insert');
          return;
        }
      }
      send();
      return;
    }
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      if (sendBlocked) return;
      send();
      return;
    }
    if (e.key === 'Escape' && busy) {
      e.preventDefault();
      onInterrupt?.();
    }
  }

  function patchAttachment(id: string, patch: Partial<Attachment>) {
    patchStoredAttachment(uploadKey, id, patch);
  }

  function removeAttachment(id: string) {
    if (pendingSubmit) return;
    updateAttachments(uploadKey, (prev) => {
      const target = prev.find((a) => a.id === id);
      if (target?.controller) target.controller.abort();
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((a) => a.id !== id);
    });
  }

  function startUpload(file: File) {
    if (!cwd || pendingSubmit) return;
    if (file.size > MAX_FILE_BYTES) {
      const id = crypto.randomUUID();
      updateAttachments(uploadKey, (prev) => [
        ...prev,
        {
          id,
          name: file.name,
          size: file.size,
          mime: file.type || 'application/octet-stream',
          status: 'error',
          progress: 0,
          error: `File exceeds ${formatBytes(MAX_FILE_BYTES)} limit`,
        },
      ]);
      return;
    }

    const id = crypto.randomUUID();
    const controller = new AbortController();
    const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined;

    updateAttachments(uploadKey, (prev) => [
      ...prev,
      {
        id,
        name: file.name,
        size: file.size,
        mime: file.type || 'application/octet-stream',
        status: 'uploading',
        progress: 0,
        controller,
        previewUrl,
      },
    ]);

    void compressImageForUpload(file)
      .then((uploadFile) => {
        if (controller.signal.aborted) throw uploadAbortedError();
        if (uploadFile !== file) {
          if (previewUrl) URL.revokeObjectURL(previewUrl);
          patchAttachment(id, {
            name: uploadFile.name,
            size: uploadFile.size,
            mime: uploadFile.type || 'application/octet-stream',
            previewUrl: uploadFile.type.startsWith('image/') ? URL.createObjectURL(uploadFile) : undefined,
          });
        }
        return uploadFile;
      })
      .then((uploadFile) =>
        uploadFn(uploadFile, {
          signal: controller.signal,
          onProgress: (fraction) => patchAttachment(id, { progress: fraction }),
        })
      )
      .then((resolved) => {
        if (controller.signal.aborted) return;
        patchAttachment(id, {
          status: 'ready',
          progress: 1,
          path: resolved.path,
          name: resolved.name,
          size: resolved.size,
          mime: resolved.mime,
          controller: undefined,
        });
      })
      .catch((err: Error) => {
        if (err.name === 'AbortError') return; // user cancelled — chip already gone
        patchAttachment(id, {
          status: 'error',
          error: err.message ?? 'Upload failed',
          controller: undefined,
        });
      });
  }

  function uploadAbortedError(): Error {
    const err = new Error('upload aborted');
    err.name = 'AbortError';
    return err;
  }

  function ingestFiles(files: FileList | File[] | null | undefined) {
    if (!files) return;
    const arr = Array.from(files);
    if (arr.length === 0) return;
    for (const f of arr) startUpload(f);
  }

  // Stash ``ingestFiles`` in a ref so the imperative-handle effect
  // below can publish a stable proxy without re-running on every
  // render (``ingestFiles`` is a fresh closure each pass).
  const ingestFilesRef = React.useRef(ingestFiles);
  ingestFilesRef.current = ingestFiles;
  // Same trick for ``setText`` — it captures ``setValue`` /
  // ``textareaRef``, both of which are stable, but we keep the ref
  // indirection so the published handle never changes identity
  // across renders.
  const setTextRef = React.useRef<(text: string, mode?: 'replace' | 'append') => void>(() => undefined);
  setTextRef.current = (text, mode = 'replace') => {
    setValue((prev) => {
      if (mode === 'append' && prev.length > 0) {
        return `${prev.replace(/\s+$/, '')}\n${text}`;
      }
      return text;
    });
    // Programmatic text (starter-prompt fallback) is plain prose. A
    // replace wipes the slate; an append leaves existing confirmed spans
    // intact since the new text lands after them.
    if (mode !== 'append') setConfirmedMentions([]);
    // Focus + caret-to-end happen on the next frame so React has
    // committed the new value to the DOM before we read its length.
    requestAnimationFrame(() => {
      const ta = textareaRef.current;
      if (!ta || ta.disabled) return;
      ta.focus({ preventScroll: true });
      const end = ta.value.length;
      ta.setSelectionRange(end, end);
      setCursorIndex(end);
    });
  };
  React.useEffect(() => {
    if (!apiRef) return;
    apiRef.current = {
      ingestFiles: (files) => ingestFilesRef.current(files),
      setText: (text, mode) => setTextRef.current(text, mode),
    };
    return () => {
      apiRef.current = null;
    };
  }, [apiRef]);

  function handlePaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    if (uploadsDisabled) return;
    const files = e.clipboardData?.files;
    if (files && files.length > 0) {
      // Only swallow the paste when the clipboard carried real file
      // bytes — otherwise we'd block ordinary text paste from any
      // source that happens to populate ``files`` opportunistically.
      e.preventDefault();
      ingestFiles(files);
      return;
    }

    const pastedText = e.clipboardData?.getData('text/plain') ?? '';
    if (!shouldAttachPastedText(pastedText)) return;
    e.preventDefault();
    startUpload(new File([pastedText], pastedSnippetFilename(), { type: 'text/plain' }));
  }

  const defaultPlaceholder = 'Ask Antioch Agent anything';
  const hasUploadingAttachments = attachments.some((a) => a.status === 'uploading');
  const hasReadyAttachments = attachments.some((a) => a.status === 'ready');
  const canSend =
    !composerDisabled && !sendBlocked && !hasUploadingAttachments && (value.trim().length > 0 || hasReadyAttachments);
  const planModeActive = permissionMode === 'plan';
  const sendTitle = pendingSubmit ? 'Sending...' : (sendBlockedReason ?? 'Send');

  return (
    <div
      // ``[scrollbar-gutter:stable]`` reserves the same scrollbar-
      // width gutter that ``TurnStream``'s scroll container reserves
      // above us. Without it, the input's ``max-w-[860px]`` column
      // would float ~7-8px to the right of the chat column on wide
      // panels, since the chat column is centered inside a parent
      // that's already ~15px narrower than ours due to its scrollbar
      // gutter.
      //
      // The vertical breathing room *between* the chat and the input
      // when scrolled to the bottom lives on the chat scroll
      // container (``pb-12`` in ``TurnStream``), not here — this
      // wrapper only contributes a small gap so the rounded panel
      // doesn't kiss whatever message happens to be at the bottom of
      // the viewport when the user is scrolled up.
      className={cn(
        'relative z-20 flex min-w-0 flex-none flex-col gap-2 overflow-visible bg-transparent px-7 pt-2 pb-6 [scrollbar-gutter:stable]',
        className
      )}
    >
      <div className="relative mx-auto w-full max-w-[860px]">
        {/* Completion picker floats above the compose panel so it
            overlays the chat instead of changing the chat/input
            layout every time "/" or "@" opens. */}
        {completionOpen && completionMode && (
          <div className="absolute right-0 bottom-full left-0 z-50 mb-2">
            <CompletionMenu
              mode={completionMode}
              items={completionItems}
              activeIndex={completionIndex}
              onHover={setCompletionIndex}
              onSelect={(item) => pickCompletion(item, item.mode === 'slash' ? 'send' : 'insert')}
            />
          </div>
        )}
        {/* ``overflow-hidden`` clips the textarea's scrollbar to the
            ``rounded-2xl`` corner shape so the bar can never poke
            out of the rounded top-right when the input grows past
            its max height. The effort chip below uses a Radix dropdown
            that portals to ``document.body``, so clipping the panel
            doesn't risk its menu. */}
        <div
          className={cn(
            // ``@container/composer`` lets the footer pills collapse
            // their labels via ``@max-[460px]/composer:hidden`` when
            // the composer (and therefore the chat pane) gets narrow
            // enough that the model/effort/permission chips would
            // collide with the right-edge stop/send buttons. The
            // chip icons + tooltips stay visible so the controls
            // remain discoverable and accessible at any width.
            '@container/composer',
            'relative isolate flex w-full min-w-0 flex-col overflow-hidden rounded-2xl border bg-white/95 shadow-[0_10px_28px_-22px_rgba(15,23,42,0.45)] backdrop-blur-xl transition-[border-color,box-shadow,background-color] duration-200 dark:bg-[#14171b]/95 dark:shadow-[0_14px_34px_-26px_rgba(0,0,0,0.7)]',
            planModeActive
              ? 'border-[#245f9f]/40 shadow-[0_0_0_1px_rgba(36,95,159,0.16),0_10px_28px_-22px_rgba(15,23,42,0.45)] focus-within:border-[#245f9f]/55 dark:border-[#6fa3dc]/45 dark:shadow-[0_0_0_1px_rgba(111,163,220,0.22),0_14px_34px_-26px_rgba(0,0,0,0.7)] dark:focus-within:border-[#8bb7e6]/60'
              : 'border-gray-200 focus-within:border-gray-300 dark:border-white/10 dark:focus-within:border-white/20'
          )}
        >
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-4 top-0 h-px bg-gradient-to-r from-transparent via-gray-300/80 to-transparent dark:via-white/20"
          />
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-1.5 px-3 pt-2.5">
              {attachments.map((att) => (
                <AttachmentChip
                  key={att.id}
                  attachment={att}
                  disabled={inputLocked}
                  onRemove={() => removeAttachment(att.id)}
                />
              ))}
            </div>
          )}
          {/* Decoration mirror + textarea share identical typography
            and padding so slash/@ badges line up pixel-for-pixel
            with the user's caret. The mirror sits behind the
            textarea (``inset-0``); when decorated tokens exist the
            textarea's text goes transparent so the mirror's spans
            show through, while the caret and selection layer stay
            native. */}
          <div className="relative bg-gradient-to-b from-white to-gray-50/35 dark:from-white/[0.035] dark:to-transparent">
            {hasInputDecorations && (
              <div
                ref={mirrorRef}
                aria-hidden
                className={cn(
                  'pointer-events-none absolute inset-0 overflow-hidden whitespace-pre-wrap break-words px-4 pt-3 pb-2 text-[14.5px] text-gray-900 leading-relaxed dark:text-gray-100'
                )}
              >
                <DecoratedInputText value={value} decorations={inputDecorations} />
                {/* Trailing zero-width-space so a value ending in
                  "\n" still reserves its last visual line in the
                  mirror — otherwise the decorated text would jump up
                  by one row whenever the user lands on a fresh
                  line. */}
                {'\u200b'}
              </div>
            )}
            {/* Whitespace-only placeholder fallback. The native
              ``placeholder`` attribute hides the moment the
              textarea has any character at all — including
              Shift+Enter newlines — which leaves a tall empty-
              looking input with no hint of what to type. We
              render our own overlay span when ``value`` is
              non-empty but trims to nothing, anchored to the
              same top-left position the native placeholder
              uses, so the prompt stays visible until the user
              actually types content. */}
            {value.length > 0 && value.trim().length === 0 && (
              <div
                aria-hidden
                className={cn(
                  'pointer-events-none absolute inset-0 select-none px-4 pt-3 pb-2 text-[14.5px] leading-relaxed',
                  'text-gray-400 dark:text-gray-500'
                )}
              >
                {placeholder ?? defaultPlaceholder}
              </div>
            )}
            <textarea
              ref={textareaRef}
              value={value}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onKeyUp={readCursorIndex}
              onClick={readCursorIndex}
              onSelect={readCursorIndex}
              onPaste={handlePaste}
              onScroll={syncMirrorScroll}
              placeholder={placeholder ?? defaultPlaceholder}
              rows={1}
              // ``cols={1}`` defeats the textarea's default 20-col intrinsic
              // width so ``w-full`` + ``min-w-0`` are the only signals
              // sizing the compose surface — without it, the textarea
              // pinned a ~200 px floor on the whole pane regardless of
              // ``w-full``, and tool blocks above it appeared to refuse to
              // shrink past that width.
              cols={1}
              disabled={composerDisabled}
              className={cn(
                // ``agent-scroll`` matches the chat scroll container's
                // narrow 6px thumb so the input's scrollbar reads as
                // the same instrument when content overflows the
                // ``max-h-[400px]`` cap. The panel's ``overflow-hidden``
                // + ``rounded-2xl`` clips the bar to the corner so it
                // never spills past the top-right radius.
                'agent-scroll relative w-full min-w-0 select-text resize-none bg-transparent px-4 pt-3 pb-2 text-[14.5px] leading-relaxed outline-none',
                // ``min-h-[82px]`` opens the input at ~4 lines so the
                // compose surface reads as a paragraph-shaped target
                // instead of a single thin slit; auto-grow takes over
                // from there until the ``max-h-[400px]`` cap.
                'max-h-[400px] min-h-[82px]',
                'placeholder:text-gray-400 dark:placeholder:text-gray-500',
                // When the mirror is rendering, hide the textarea's
                // own glyphs but keep the caret + selection layer so
                // typing still feels native.
                hasInputDecorations && 'text-transparent caret-gray-900 selection:bg-blue-500/30 dark:caret-gray-100'
              )}
            />
          </div>
          {/* Footer padding matches the textarea (``px-4``) so the
            stable controls align with the placeholder
            above it. Live session metadata (active tasks, plan,
            context %, …) lives in its own right-aligned strip
            *above* this row — see ``AgentPanelOverlay`` — so this
            footer stays focused on the connection / model
            configuration that the user controls. */}
          <div className="flex select-none items-center justify-between gap-2 pt-1 pr-2 pb-2 pl-4">
            <div className="flex min-w-0 items-center gap-2">
              {/* ``@max-[460px]/composer:hidden`` collapses the pill
                  labels to icon-only the moment the composer is too
                  narrow to fit the full row alongside the right-edge
                  send/stop buttons. Tooltips and aria-labels carry
                  the readable text, so accessibility and tap targets
                  are preserved.

                  Model pill intentionally absent: we ship a single
                  model (Opus 4.8) so showing it just consumes width.
                  Reintroduce the moment we expose a second model. */}
              {effort && (
                <EffortPill
                  effort={effort}
                  onChange={onChangeEffort}
                  disabled={connectionStatus === 'connecting' || (connectionStatus === 'open' && !attached)}
                  labelClassName="@max-[460px]/composer:hidden"
                />
              )}
              <SpeedPill
                speed={speed}
                onChange={onChangeSpeed}
                disabled={connectionStatus === 'connecting' || (connectionStatus === 'open' && !attached)}
                labelClassName="@max-[460px]/composer:hidden"
              />
              {permissionMode && (
                <PermissionModePill
                  mode={permissionMode}
                  disabled={connectionStatus === 'connecting' || (connectionStatus === 'open' && !attached)}
                  onChange={canChangePermissionMode ? onChangePermissionMode : undefined}
                  labelClassName="@max-[460px]/composer:hidden"
                />
              )}
              <PaperclipButton
                disabled={uploadsDisabled}
                onClick={() => fileInputRef.current?.click()}
                tooltip={!cwd ? 'Pick a project to attach files' : 'Attach files'}
              />
              {(pendingSubmit || sendBlockedReason) && (
                // Hidden at narrow composer widths -- the disabled
                // send button is itself the "send in flight"
                // affordance, and at < 460px container the truncated
                // "Sending..." chrome was crashing into the action
                // buttons (the original report).
                <span className="@max-[460px]/composer:hidden truncate text-[11px] text-gray-500 dark:text-gray-400">
                  {pendingSubmit ? 'Sending...' : sendBlockedReason}
                </span>
              )}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                onChange={(e) => {
                  ingestFiles(e.target.files);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
              />
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {busy && onInterrupt ? (
                <button
                  type="button"
                  onClick={onInterrupt}
                  aria-label="Stop"
                  title="Stop"
                  className={cn(
                    'flex size-8 shrink-0 cursor-pointer select-none items-center justify-center rounded-lg',
                    'bg-gray-100 text-gray-500 transition-[background-color,color,transform] duration-150',
                    'hover:bg-gray-200/80 hover:text-gray-700 active:scale-[0.98]',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300',
                    'dark:bg-white/[0.06] dark:text-gray-400 dark:focus-visible:ring-white/20 dark:hover:bg-white/[0.1] dark:hover:text-gray-200'
                  )}
                >
                  <Square className="size-3" strokeWidth={2.4} />
                </button>
              ) : null}
              <button
                type="button"
                onClick={send}
                disabled={!canSend}
                aria-label="Send"
                title={sendTitle}
                className={cn(
                  'flex h-8 shrink-0 select-none items-center justify-center rounded-full transition-[background-color,box-shadow,transform,opacity] duration-150',
                  'w-8',
                  canSend
                    ? 'cursor-pointer bg-gray-800 text-white shadow-sm hover:scale-[1.03] hover:bg-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 dark:bg-white dark:text-gray-900 dark:focus-visible:ring-white/25 dark:hover:bg-gray-200'
                    : 'cursor-not-allowed bg-gray-200 text-gray-400 dark:bg-white/10 dark:text-gray-500'
                )}
              >
                <ArrowUp className="size-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface AttachmentChipProps {
  attachment: Attachment;
  disabled?: boolean;
  onRemove: () => void;
}

function AttachmentChip({ attachment, disabled = false, onRemove }: AttachmentChipProps) {
  const descriptor = { name: attachment.name, path: attachment.path, mime: attachment.mime };
  const isImage = isImageAttachment(descriptor);
  const errored = attachment.status === 'error';
  const uploading = attachment.status === 'uploading';
  const openable = attachment.status === 'ready' && attachment.path != null;

  function openAttachment() {
    if (!attachment.path) return;
    const url = attachmentUploadUrl(attachment.path);
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  return (
    <div
      className={cn(
        'group relative flex max-w-[260px] select-none items-center gap-2 overflow-hidden rounded-lg border bg-white px-2 py-1.5',
        'border-gray-200 dark:border-white/10 dark:bg-white/[0.03]',
        errored && 'border-red-300/80 dark:border-red-400/40'
      )}
      title={errored ? attachment.error : (attachment.path ?? attachment.name)}
    >
      <button
        type="button"
        onClick={openAttachment}
        disabled={!openable}
        className={cn(
          'flex min-w-0 flex-1 items-center gap-2 text-left',
          openable ? 'cursor-pointer' : 'cursor-default'
        )}
      >
        <div
          className={cn(
            'flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-md',
            isImage ? 'bg-gray-100 dark:bg-white/5' : 'bg-gray-100 text-gray-500 dark:bg-white/5 dark:text-gray-400',
            errored && 'bg-red-50 text-red-500 dark:bg-red-500/10 dark:text-red-300'
          )}
        >
          {isImage && attachment.previewUrl ? (
            <img alt="" src={attachment.previewUrl} className="size-7 object-cover" />
          ) : isImage ? (
            <ImageIcon className="size-3.5" />
          ) : (
            <FileText className="size-3.5" />
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="truncate text-[13px] text-gray-800 leading-tight dark:text-gray-100">{attachment.name}</div>
          <div className="text-[11.5px] text-gray-500 leading-tight dark:text-gray-400">
            {errored
              ? attachment.error
              : uploading
                ? `${formatBytes(attachment.size)} · ${Math.round(attachment.progress * 100)}%`
                : formatBytes(attachment.size)}
          </div>
          {uploading && (
            <div className="mt-1 h-0.5 overflow-hidden rounded-full bg-gray-200 dark:bg-white/10">
              <div
                className="h-full bg-gray-700 transition-all duration-150 dark:bg-gray-200"
                style={{ width: `${Math.max(2, attachment.progress * 100)}%` }}
              />
            </div>
          )}
        </div>
      </button>
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        aria-label="Remove attachment"
        className={cn(
          'flex size-5 shrink-0 select-none items-center justify-center rounded text-gray-400',
          disabled
            ? 'cursor-not-allowed opacity-50'
            : 'cursor-pointer hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-white/10 dark:hover:text-gray-200',
          'dark:text-gray-500'
        )}
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}

interface PaperclipButtonProps {
  disabled: boolean;
  tooltip: string;
  onClick: () => void;
}

function PaperclipButton({ disabled, tooltip, onClick }: PaperclipButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={tooltip}
      aria-label={tooltip}
      className={cn(
        'flex size-6 shrink-0 select-none items-center justify-center rounded-md text-gray-400 transition-colors',
        disabled
          ? 'cursor-not-allowed opacity-40'
          : 'cursor-pointer hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-white/10 dark:hover:text-gray-200'
      )}
    >
      <Paperclip className="size-3.5" />
    </button>
  );
}

interface CompletionMenuProps {
  mode: CompletionMode;
  items: CompletionItem[];
  activeIndex: number;
  onHover: (index: number) => void;
  onSelect: (item: CompletionItem) => void;
}

function CompletionMenu({ mode, items, activeIndex, onHover, onSelect }: CompletionMenuProps) {
  const isSlash = mode === 'slash';
  const title = isSlash ? 'Commands' : 'Files and folders';
  const Icon = isSlash ? TerminalSquare : Folder;
  const listRef = React.useRef<HTMLUListElement>(null);
  const itemRefs = React.useRef<Array<HTMLButtonElement | null>>([]);

  React.useLayoutEffect(() => {
    const list = listRef.current;
    const active = itemRefs.current[activeIndex];
    if (!list || !active) return;

    const activeTop = active.offsetTop;
    const activeBottom = activeTop + active.offsetHeight;
    const visibleTop = list.scrollTop;
    const visibleBottom = visibleTop + list.clientHeight;
    const padding = 6;

    if (activeTop < visibleTop + padding) {
      list.scrollTop = Math.max(0, activeTop - padding);
    } else if (activeBottom > visibleBottom - padding) {
      list.scrollTop = activeBottom - list.clientHeight + padding;
    }
  }, [activeIndex]);

  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border border-gray-200/80 bg-white/[0.98] shadow-[0_16px_48px_-28px_rgba(15,23,42,0.45)] backdrop-blur-xl',
        'dark:border-white/10 dark:bg-[#171a1f]/[0.98] dark:shadow-[0_18px_56px_-34px_rgba(0,0,0,0.9)]'
      )}
      role="listbox"
      aria-label={title}
    >
      <div className="flex items-center gap-1.5 border-gray-200/70 border-b bg-gray-50 px-3 py-1.5 font-medium text-[10.5px] text-gray-500 uppercase tracking-[0.055em] dark:border-white/10 dark:bg-[#171a1f] dark:text-gray-400">
        <Icon className="size-3 opacity-70" />
        {title}
      </div>
      <ul ref={listRef} className="agent-scroll max-h-44 overflow-y-auto p-1.5">
        {items.map((item, i) => {
          const isActive = i === activeIndex;
          return (
            <li key={item.id}>
              <button
                type="button"
                ref={(node) => {
                  itemRefs.current[i] = node;
                }}
                role="option"
                aria-selected={isActive}
                onMouseMove={() => {
                  if (i !== activeIndex) onHover(i);
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onSelect(item);
                }}
                className={cn(
                  'flex w-full cursor-pointer select-none items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors duration-100',
                  isActive
                    ? 'bg-gray-100 text-gray-950 dark:bg-white/[0.08] dark:text-gray-50'
                    : 'text-gray-700 dark:text-gray-200'
                )}
              >
                <span
                  className={cn(
                    'shrink-0 rounded-md px-1.5 py-0.5 font-medium text-[11px] leading-none',
                    isSlash
                      ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-200/80 dark:bg-blue-400/10 dark:text-blue-200 dark:ring-blue-300/20'
                      : 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/80 dark:bg-emerald-400/10 dark:text-emerald-200 dark:ring-emerald-300/20'
                  )}
                >
                  {isSlash ? '/' : '@'}
                </span>
                <span className="min-w-0 flex-1 truncate font-medium text-[13px]">
                  {item.label}
                  {item.isDir ? <span className="text-gray-400 dark:text-gray-500">/</span> : null}
                </span>
                {isActive && (
                  <span className="hidden shrink-0 font-medium text-[10px] text-gray-400 uppercase tracking-wider sm:inline dark:text-gray-500">
                    {isSlash ? '↵ send' : '↵ insert'}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

interface PathCompletionRequest {
  directory: string;
  prefix: string;
  insertPrefix: string;
}

function slashTriggerAt(value: string, cursor: number): SlashTrigger | null {
  const before = value.slice(0, cursor);
  const after = value.slice(cursor);
  if (/^\S/.test(after)) return null;
  const match = before.match(/^\/([^\s/]*)$/);
  if (!match) return null;
  return {
    query: match[1] ?? '',
    start: 0,
    end: cursor,
  };
}

function pathTriggerAt(value: string, cursor: number): PathTrigger | null {
  const before = value.slice(0, cursor);
  const after = value.slice(cursor);
  if (/^\S/.test(after)) return null;
  const match = before.match(/(^|\s)@([^\s@]*)$/);
  if (!match) return null;
  const query = match[2] ?? '';
  return {
    query,
    start: before.length - query.length - 1,
    end: cursor,
  };
}

function pathCompletionRequest(cwd: string, query: string): PathCompletionRequest {
  const slash = query.lastIndexOf('/');
  if (query.startsWith('/')) {
    return {
      directory: slash <= 0 ? '/' : query.slice(0, slash),
      prefix: slash <= 0 ? query.slice(1) : query.slice(slash + 1),
      insertPrefix: slash < 0 ? '' : query.slice(0, slash + 1),
    };
  }
  if (query.startsWith('~')) {
    return {
      directory: slash <= 0 ? query || '~' : query.slice(0, slash),
      prefix: slash <= 0 ? query.slice(1) : query.slice(slash + 1),
      insertPrefix: slash < 0 ? '' : query.slice(0, slash + 1),
    };
  }

  const directoryPart = slash >= 0 ? query.slice(0, slash) : '';
  return {
    directory: normalizeWorkspacePath(cwd, directoryPart || '.'),
    prefix: slash >= 0 ? query.slice(slash + 1) : query,
    insertPrefix: slash >= 0 ? query.slice(0, slash + 1) : '',
  };
}

function normalizeWorkspacePath(cwd: string, path: string): string {
  if (path.startsWith('~')) return path;
  const parts = path.startsWith('/') ? [] : cwd.split('/').filter(Boolean);
  for (const part of path.split('/')) {
    if (!part || part === '.') continue;
    if (part === '..') parts.pop();
    else parts.push(part);
  }
  return `/${parts.join('/')}`.replace(/\/+$/, '') || '/';
}

function comparePathEntries(a: AgentDirectoryEntry, b: AgentDirectoryEntry): number {
  if (a.is_dir !== b.is_dir) return a.is_dir ? -1 : 1;
  return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
}

function pathCompletionItem(entry: AgentDirectoryEntry, insertPrefix: string): CompletionItem {
  return {
    id: `path:${entry.path}`,
    mode: 'path',
    label: entry.name,
    value: `${insertPrefix}${entry.name}${entry.is_dir ? '/' : ''}`,
    isDir: entry.is_dir,
  };
}

function resolveMentionPaths(text: string, cwd: string, ranges: ReadonlyArray<MentionRange>): string[] {
  const paths: string[] = [];
  const seen = new Set<string>();
  for (const range of ranges) {
    if (range.start < 0 || range.end > text.length || text[range.start] !== '@') continue;
    const rawToken = text.slice(range.start + 1, range.end);
    const token = trimMentionToken(rawToken);
    if (!token) continue;
    const isDirectoryHint = /\/+$/.test(rawToken.replace(/[),.;:]+$/g, ''));
    const normalized = normalizeWorkspacePath(cwd, token);
    const resolved = isDirectoryHint && normalized !== '/' ? `${normalized}/` : normalized;
    if (!seen.has(resolved)) {
      seen.add(resolved);
      paths.push(resolved);
    }
  }
  return paths;
}

/**
 * Whether a ``DataTransfer`` payload carries actual file bytes
 * (rather than HTML / text snippets). Exported so the overlay's
 * pane-wide drop handler shares the same gate the input does.
 */
export function dataTransferHasFiles(dt: DataTransfer | null | undefined): boolean {
  if (!dt) return false;
  for (const t of Array.from(dt.types ?? [])) {
    if (t === 'Files') return true;
  }
  return false;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
